import {
  Alert,
  Box,
  Button,
  Collapsible,
  Grid,
  HStack,
  IconButton,
  Image,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { toasterApi } from '@ui';
import React from 'react';
import { LuChevronDown, LuCopy, LuRefreshCw } from 'react-icons/lu';
import { TrackingType } from '@/types';
import {
  KARAS_RED,
  REPORT_BORDER,
  REPORT_FONT,
  REPORT_GRID,
  REPORT_HEADER_BG,
  REPORT_SUBTLE,
  REPORT_TEXT,
  formatBytes,
} from '../utils';

const NOT_RECORDED = 'Not recorded';

const kindHint: Record<string, string | undefined> = {
  aggregate: 'Frame-type totals',
  restricted: 'Restricted',
  unavailable: 'Source unavailable',
  manual: 'Entered in Tracking',
};

const SUMMARY_LABELS = new Set([
  'Project',
  'Release',
  'Frame tag',
  'Unit',
  'Lot quantity',
  'Status',
  'Crate number',
  'Work order status',
]);

const REFERENCE_LABELS = new Set([
  'Tracking code',
  'Numeric code',
  'Item type',
  'Tracked as',
  'Identity status',
  'Scope',
  'Registered',
  'Registered by',
  'Permanent link',
  'Permanent unit number',
  'Frame entered',
  'Unit registered',
  'Created',
  'Created by',
]);

const GROUP_KEYS: { id: string; title: string; keys: string[] }[] = [
  {
    id: 'production',
    title: 'Production and fabrication',
    keys: [
      'production',
      'production_history',
      'parts',
      'drawing',
      'fabrication',
      'fabrication_log',
    ],
  },
  {
    id: 'logistics',
    title: 'Packing, shipping, and installation',
    keys: [
      'packing',
      'crate_contents',
      'crate_shipments',
      'shipment_items',
    ],
  },
  { id: 'quality', title: 'Quality and history', keys: ['quality'] },
];

const KEEP_EMPTY = new Set(["This unit's own completion"]);

const hasValue = (field: TrackingType.ReportField) =>
  field.value !== null && field.value !== '';

const showField = (field: TrackingType.ReportField) =>
  hasValue(field) || KEEP_EMPTY.has(field.label);

const findField = (sections: TrackingType.ReportSection[], label: string) => {
  for (const section of sections) {
    const field = section.fields.find((f) => f.label === label && hasValue(f));
    if (field) return field;
  }
  return null;
};

export const FieldGrid: React.FC<{
  fields: TrackingType.ReportField[];
  skipLabels?: Set<string>;
}> = ({ fields, skipLabels }) => {
  const shown = fields.filter(
    (f) => showField(f) && !skipLabels?.has(f.label),
  );
  if (!shown.length) return null;
  return (
    <Grid
      templateColumns={{ base: '1fr', md: '1fr 1fr' }}
      columnGap={6}
      rowGap={0}
      as="dl"
    >
      {shown.map((f, i) => (
        <Grid
          key={`${f.label}-${i}`}
          templateColumns="minmax(110px, 38%) 1fr"
          gap={2}
          py={1.5}
          borderBottom="1px solid"
          borderColor={REPORT_GRID}
          className="report-avoid-break"
        >
          <Text as="dt" fontSize="xs" color={REPORT_SUBTLE}>
            {f.label}
          </Text>
          <Text
            as="dd"
            fontSize="sm"
            color={hasValue(f) ? REPORT_TEXT : REPORT_SUBTLE}
            wordBreak="break-word"
            fontStyle={hasValue(f) ? undefined : 'italic'}
          >
            {hasValue(f) ? f.value : NOT_RECORDED}
          </Text>
        </Grid>
      ))}
    </Grid>
  );
};

export const DataTable: React.FC<{ table: TrackingType.ReportTable }> = ({
  table,
}) => {
  if (!table.rows.length) {
    return (
      <Text fontSize="sm" color={REPORT_SUBTLE} fontStyle="italic">
        {NOT_RECORDED}.
      </Text>
    );
  }
  return (
    <Box
      overflowX="auto"
      borderWidth="1px"
      borderColor={REPORT_GRID}
      borderRadius="sm"
    >
      <Table.Root size="sm" className="report-table" bg="#FFFFFF">
        <Table.Header>
          <Table.Row bg={REPORT_HEADER_BG}>
            {table.columns.map((c) => (
              <Table.ColumnHeader
                key={c}
                fontSize="xs"
                fontWeight="bold"
                color={REPORT_TEXT}
                bg={REPORT_HEADER_BG}
                borderColor={REPORT_GRID}
                whiteSpace="nowrap"
              >
                {c}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {table.rows.map((row, i) => (
            <Table.Row key={i} bg="#FFFFFF" className="report-avoid-break">
              {row.map((cell, j) => (
                <Table.Cell
                  key={j}
                  fontSize="xs"
                  color={REPORT_TEXT}
                  bg="#FFFFFF"
                  borderColor={REPORT_GRID}
                  verticalAlign="top"
                  wordBreak="break-word"
                >
                  {cell === null || cell === '' ? '-' : String(cell)}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      {table.total > table.rows.length && (
        <Text fontSize="xs" color={REPORT_SUBTLE} px={2} py={1}>
          Showing {table.rows.length} of {table.total}.
        </Text>
      )}
    </Box>
  );
};

const Disclosure: React.FC<{
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, summary, defaultOpen, children }) => (
  <Collapsible.Root defaultOpen={defaultOpen} className="report-section">
    <Collapsible.Trigger asChild>
      <Button
        variant="ghost"
        w="100%"
        justifyContent="space-between"
        px={0}
        py={2}
        h="auto"
        color={REPORT_TEXT}
        bg="white"
        fontWeight="bold"
        borderBottomWidth="1px"
        borderColor={REPORT_GRID}
        borderRadius={0}
        _hover={{ bg: '#F8FAFC' }}
        _expanded={{ bg: 'white' }}
      >
        <HStack gap={2} minW={0}>
          <LuChevronDown />
          <Text as="span">{title}</Text>
          {summary && (
            <Text as="span" fontWeight="normal" color={REPORT_SUBTLE} fontSize="sm">
              {summary}
            </Text>
          )}
        </HStack>
      </Button>
    </Collapsible.Trigger>
    <Collapsible.Content className="report-print-body">{children}</Collapsible.Content>
  </Collapsible.Root>
);

export const ReportSection: React.FC<{
  section: TrackingType.ReportSection;
  skipLabels?: Set<string>;
}> = ({ section, skipLabels }) => {
  const hint = kindHint[section.kind];
  const fields = section.fields.filter(
    (f) => showField(f) && !skipLabels?.has(f.label),
  );
  const tables = section.tables.filter((t) => t.rows.length > 0);
  const empty = !fields.length && !tables.length && section.kind !== 'restricted' && section.kind !== 'unavailable';
  return (
    <Box
      as="section"
      aria-labelledby={`section-${section.key}`}
      className="report-section"
    >
      <HStack gap={2} mb={1} flexWrap="wrap">
        <Text
          id={`section-${section.key}`}
          as="h3"
          fontWeight="bold"
          fontSize="md"
          color={REPORT_TEXT}
        >
          {section.title}
        </Text>
        {hint && (
          <Text fontSize="xs" color={REPORT_SUBTLE}>
            {hint}
          </Text>
        )}
      </HStack>
      {section.source_updated_at && (
        <Text fontSize="xs" color={REPORT_SUBTLE} mb={1}>
          Source updated {section.source_updated_at}
        </Text>
      )}
      {section.kind === 'restricted' ? (
        <Text fontSize="sm" color={REPORT_SUBTLE}>
          {section.note}
        </Text>
      ) : (
        <Stack gap={2}>
          {empty ? (
            <Text fontSize="sm" color={REPORT_SUBTLE}>
              Nothing is recorded in this section.
            </Text>
          ) : (
            <FieldGrid fields={fields} />
          )}
          {tables.map((t, i) => (
            <Stack key={i} gap={1}>
              <DataTable table={t} />
              {t.note && (
                <Text fontSize="xs" color={REPORT_SUBTLE}>
                  {t.note}
                </Text>
              )}
            </Stack>
          ))}
          {section.note && (
            <Text fontSize="xs" color={REPORT_SUBTLE}>
              {section.note}
            </Text>
          )}
        </Stack>
      )}
    </Box>
  );
};

interface ItemReportViewProps {
  report: TrackingType.ItemReport;
  actions?: React.ReactNode;
  statusBanner?: React.ReactNode;
  onLoadMoreHistory?: () => void;
  historyItems?: TrackingType.ReportEvent[];
  historyTotal?: number;
  loadingHistory?: boolean;
  onOpenAttachment?: (attachment: TrackingType.ReportAttachment) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}

/** Branded latest-data report. Mirrors the Karas packing/shipment report design. */
export const ItemReportView: React.FC<ItemReportViewProps> = ({
  report,
  actions,
  statusBanner,
  onLoadMoreHistory,
  historyItems,
  historyTotal,
  loadingHistory,
  onOpenAttachment,
  onRefresh,
  refreshing,
}) => {
  const { identity } = report;
  const events = historyItems ?? report.history.items;
  const total = historyTotal ?? report.history.total;
  const sections = report.sections.filter((s) => s.key !== 'identity');
  const identitySection = report.sections.find((s) => s.key === 'identity');
  const summary = ['Project', 'Release', 'Frame tag', 'Unit', 'Lot quantity', 'Crate number', 'Status', 'Work order status']
    .map((label) => findField(report.sections, label))
    .filter((f): f is TrackingType.ReportField => Boolean(f));
  const seen = new Set<string>();
  const summaryFields = summary.filter((f) => {
    if (seen.has(f.label)) return false;
    seen.add(f.label);
    return true;
  });
  const grouped = new Set(GROUP_KEYS.flatMap((g) => g.keys));
  const overview = sections.filter(
    (s) => !grouped.has(s.key) && s.key !== 'manual',
  );
  const manual = sections.find((s) => s.key === 'manual');
  const conditions: { status: 'error' | 'warning' | 'info'; text: string }[] = [];
  if (identity.state === 'VOID')
    conditions.push({ status: 'error', text: 'This identity is void.' });
  if (identity.state === 'MERGED')
    conditions.push({
      status: 'info',
      text: 'This code was merged. It still opens this report.',
    });
  if (identity.state === 'ORPHANED')
    conditions.push({
      status: 'warning',
      text: 'This item is orphaned and is no longer in the planned quantity.',
    });
  const unit = findField(report.sections, 'Unit');
  if (unit?.value?.toLowerCase().startsWith('orphaned'))
    conditions.push({ status: 'warning', text: unit.value });
  const releaseStatus = findField(report.sections, 'Release status');
  if (releaseStatus && /supersed|archiv|void|obsolete/i.test(releaseStatus.value ?? ''))
    conditions.push({
      status: 'warning',
      text: `Release status: ${releaseStatus.value}`,
    });
  const quality = report.sections.find((s) => s.key === 'quality');
  const qaRows = (quality?.tables[0]?.rows ?? []).filter((row) =>
    /fail|reject/i.test(String(row[2] ?? '')),
  );
  if (qaRows.length)
    conditions.push({
      status: 'error',
      text: `${qaRows.length} quality result${qaRows.length === 1 ? '' : 's'} ${qaRows.length === 1 ? 'is' : 'are'} failed or rejected.`,
    });
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(identity.display_code);
      toasterApi({
        type: 'success',
        status: 200,
        code: 'Copied',
        message: 'Tracking code copied',
      });
    } catch {
      toasterApi({
        type: 'error',
        status: 0,
        code: 'Copy',
        message: 'The tracking code could not be copied',
      });
    }
  };
  return (
    <Box
      className="tracking-report"
      bg="white"
      color={REPORT_TEXT}
      fontFamily={REPORT_FONT}
      borderRadius={{ base: 0, md: 'md' }}
      borderWidth={{ base: 0, md: '1px' }}
      borderColor={REPORT_BORDER}
      p={{ base: 4, md: 8 }}
      maxW="960px"
      mx="auto"
      w="100%"
    >
      <HStack justify="space-between" align="flex-start" gap={4}>
        <Box minW={0}>
          <Text fontSize="sm" color={REPORT_SUBTLE}>
            Item Tracking Report
          </Text>
          <Text
            as="h1"
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="bold"
            color={REPORT_TEXT}
            lineHeight="short"
            wordBreak="break-word"
          >
            {identity.title}
          </Text>
          <HStack gap={2} mt={2} flexWrap="wrap">
            <Text fontSize="sm" fontFamily="mono">
              {identity.display_code}
            </Text>
            <IconButton
              aria-label="Copy tracking code"
              size="xs"
              variant="outline"
              color={REPORT_TEXT}
              borderColor="#94A3B8"
              bg="white"
              className="no-print"
              onClick={copyCode}
            >
              <LuCopy />
            </IconButton>
            <Text fontSize="sm" color={REPORT_SUBTLE}>
              {identity.kind_label}
            </Text>
            <Text fontSize="sm">
              Identity: {identity.state_label}
            </Text>
          </HStack>
        </Box>
        <Image
          src="/logo-karas.png"
          alt="Karas & Karas Glass Company Inc."
          maxW={{ base: '110px', md: '160px' }}
          h="auto"
          objectFit="contain"
          flexShrink={0}
        />
      </HStack>
      <Box borderBottom="2px solid" borderColor={KARAS_RED} my={3} />

      {statusBanner}
      {conditions.map((c) => (
        <Alert.Root key={c.text} status={c.status} mb={2}>
          <Alert.Indicator />
          <Alert.Title>{c.text}</Alert.Title>
        </Alert.Root>
      ))}
      {identity.scanned_code && (
        <Text fontSize="sm" color={REPORT_SUBTLE} mb={2}>
          Scanned code {identity.scanned_code} was merged into this item. It
          still opens this report.
        </Text>
      )}

      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', sm: 'center' }}
        mb={3}
        className="no-print"
        gap={2}
      >
        <Text fontSize="sm">
          <b>Latest information as of {report.as_of}</b>
        </Text>
        {onRefresh && (
          <Button
            size="sm"
            variant="outline"
            color={REPORT_TEXT}
            borderColor="#94A3B8"
            bg="white"
            onClick={onRefresh}
            loading={refreshing}
          >
            <LuRefreshCw /> Refresh
          </Button>
        )}
      </Stack>

      {summaryFields.length > 0 && (
        <FieldGrid fields={summaryFields} />
      )}

      {actions && (
        <Box className="no-print" my={4}>
          {actions}
        </Box>
      )}

      <Stack gap={5} mt={4}>
        {manual &&
          manual.fields.some(
            (f) => hasValue(f) && f.label !== 'Title' && !SUMMARY_LABELS.has(f.label),
          ) && (
          <ReportSection
            section={manual}
            skipLabels={new Set(['Title', ...SUMMARY_LABELS])}
          />
        )}
        {overview.map((s) => (
          <ReportSection
            key={s.key}
            section={s}
            skipLabels={new Set([...SUMMARY_LABELS, ...REFERENCE_LABELS])}
          />
        ))}
        {GROUP_KEYS.map((group) => {
          const items = sections.filter((s) => group.keys.includes(s.key));
          if (!items.length) return null;
          const populated = items.filter(
            (s) =>
              s.kind === 'restricted' ||
              s.kind === 'unavailable' ||
              Boolean(s.note) ||
              s.fields.some(hasValue) ||
              s.tables.some((t) => t.rows.length > 0),
          );
          return (
            <Disclosure
              key={group.id}
              title={group.title}
              summary={
                populated.length
                  ? `${populated.length} section${populated.length === 1 ? '' : 's'}`
                  : 'Nothing recorded'
              }
              defaultOpen={group.id === 'production' && populated.length > 0}
            >
              <Stack gap={4}>
                {items.map((s) => (
                  <ReportSection
                    key={s.key}
                    section={s}
                    skipLabels={new Set([...SUMMARY_LABELS, ...REFERENCE_LABELS])}
                  />
                ))}
              </Stack>
            </Disclosure>
          );
        })}

        <Disclosure
          title="Notes and attachments"
          summary={
            report.notes.total || report.attachments.total
              ? `${report.notes.total} note${report.notes.total === 1 ? '' : 's'} · ${report.attachments.total} file${report.attachments.total === 1 ? '' : 's'}`
              : 'None recorded'
          }
        >
        <Box as="section" className="report-section">
          <Text as="h3" fontWeight="bold" fontSize="md" mb={1}>
            Notes
          </Text>
          {report.notes.items.length ? (
            <Stack gap={2}>
              {report.notes.items.map((n) => (
                <Box
                  key={n.id}
                  borderLeft="3px solid"
                  borderColor={REPORT_BORDER}
                  pl={3}
                  className="report-avoid-break"
                >
                  <Text
                    fontSize="sm"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {n.body}
                  </Text>
                  <Text fontSize="xs" color={REPORT_SUBTLE}>
                    {n.author ?? 'Unknown'} · {n.created_at}
                  </Text>
                </Box>
              ))}
            </Stack>
          ) : (
            <Text fontSize="sm" color={REPORT_SUBTLE} fontStyle="italic">
              No notes recorded.
            </Text>
          )}
        </Box>

        <Box as="section" className="report-section">
          <Text as="h3" fontWeight="bold" fontSize="md" mb={1}>
            Photos and files
          </Text>
          {report.attachments.items.length ? (
            <Stack gap={1}>
              {report.attachments.items.map((a) => (
                <HStack
                  key={a.id}
                  justify="space-between"
                  borderBottom="1px solid"
                  borderColor={REPORT_GRID}
                  py={1.5}
                  gap={3}
                >
                  <Box minW={0}>
                    <Text fontSize="sm" wordBreak="break-word">
                      {a.filename}
                      {a.caption ? ` — ${a.caption}` : ''}
                    </Text>
                    <Text fontSize="xs" color={REPORT_SUBTLE}>
                      {a.kind === 'PHOTO' ? 'Photo' : 'File'} ·{' '}
                      {formatBytes(a.size_bytes)} · {a.uploaded_by ?? 'Unknown'}{' '}
                      · {a.uploaded_at}
                    </Text>
                  </Box>
                  {onOpenAttachment && (
                    <Button
                      size="xs"
                      variant="outline"
                      className="no-print"
                      onClick={() => onOpenAttachment(a)}
                    >
                      Open
                    </Button>
                  )}
                </HStack>
              ))}
            </Stack>
          ) : (
            <Text fontSize="sm" color={REPORT_SUBTLE}>
              No photos or files recorded.
            </Text>
          )}
        </Box>
        </Disclosure>

        <Disclosure
          title="Tracking history"
          summary={`${events.length} of ${total} events`}
          defaultOpen={events.length > 0 && events.length <= 8}
        >
        <Box as="section" className="report-section">
          <DataTable
            table={{
              columns: ['Recorded', 'Event', 'By', 'Details'],
              rows: events.map((e) => [
                e.recorded_at,
                e.type_label,
                e.actor,
                e.summary,
              ]),
              note: null,
              total: events.length,
            }}
          />
          <HStack justify="space-between" mt={1} className="no-print">
            <Text fontSize="xs" color={REPORT_SUBTLE}>
              Showing {events.length} of {total} events.
            </Text>
            {onLoadMoreHistory && events.length < total && (
              <Button
                size="xs"
                variant="outline"
                onClick={onLoadMoreHistory}
                loading={loadingHistory}
              >
                Load more
              </Button>
            )}
          </HStack>
        </Box>
        </Disclosure>

        <Disclosure title="Reference details" summary="Codes, registration, and links">
          <Stack gap={3}>
            {identitySection && (
              <FieldGrid
                fields={identitySection.fields.filter((f) =>
                  REFERENCE_LABELS.has(f.label),
                )}
              />
            )}
            {sections
              .filter((s) => s.source_updated_at)
              .map((s) => (
                <Text key={s.key} fontSize="sm" color={REPORT_SUBTLE}>
                  {s.title} updated {s.source_updated_at}
                </Text>
              ))}
          </Stack>
        </Disclosure>
      </Stack>

      <Box borderTop="1px solid" borderColor={REPORT_BORDER} mt={8} pt={2}>
        <Text fontSize="xs" color={REPORT_SUBTLE}>
          Karas & Karas Glass Company Inc. · Item Tracking Report ·{' '}
          {identity.display_code} · Latest information as of {report.as_of}
        </Text>
      </Box>
    </Box>
  );
};
